import { get, isEmpty, isNil } from 'lodash';

import { VIDEO_TYPES, VIDEO_COORDINATE_DATA_TYPES, VIDEO_OPTIONS } from './Videos';

/**

 * Gets id from video object.
 *
 * @param {object} videoObject - The video object.
 * @returns {string} The video id or an empty string if _id is undefined.
 * @throws Error if video object is undefined.
 */
export const getId = videoObject => {
  if (isNil(videoObject)) {
    throw new Error('video not defined');
  }
  return get(videoObject, '_id', '');
};

/**
 * Gets project id from video object.
 *
 * @param {object} videoObject - The video object.
 * @returns {string} The video project or an empty string if project is undefined.
 * @throws Error if video object is undefined.
 */
export const getProject = videoObject => {
  if (isNil(videoObject)) {
    throw new Error('video not defined');
  }
  return get(videoObject, 'project', '');
};

/**
 * Gets project id from video object.
 *
 * @param {object} videoObject - The video object.
 * @returns {string} The video project or an empty string if project is undefined.
 * @throws Error if video object is undefined.
 */
export const getDefaultDurationVideo = videoObject => {
  if (isNil(videoObject)) {
    throw new Error('video not defined');
  }
  const controlPoints = get(videoObject, 'controlPoints', '');
  const length = isEmpty(controlPoints) ? 0 : controlPoints.features.length;
  const duration = length !== 0 && controlPoints.features[length - 1].properties.timeUS;
  return duration;
};

/**
 * Gets video owner from video object.
 *
 * @param {object} videoObject - The video object.
 * @returns {string} The video owner or an empty string if not found.
 * @throws Error if video object is undefined.
 */
export const getOwner = videoObject => {
  if (isNil(videoObject)) {
    throw new Error('video not defined');
  }
  return get(videoObject, 'owner', '');
};

/**
 * Gets video name from video object.
 *
 * @param {object} videoObject - The video object.
 * @returns {string} The video name or an empty string if name is undefined.
 * @throws Error if video object is undefined.
 */
export const getName = videoObject => {
  if (isNil(videoObject)) {
    throw new Error('video not defined');
  }
  return get(videoObject, 'name', '');
};

/**
 * Gets video coordinates data type from video object.
 *
 * @param {object} videoObject - The video object.
 * @returns {string} The time GPS.
 * @throws Error if video object is undefined.
 */
export const getTimeGPS = videoObject => {
  if (isNil(videoObject)) {
    throw new Error('video not defined');
  }
  return get(videoObject, 'timeGPSRecording', new Date().toISOString());
};

/**
 * Gets video description from video object.
 *
 * @param {object} videoObject - The video object.
 * @returns {string} The video description or an empty string if description is undefined.
 * @throws Error if video object is undefined.
 */
export const getDescription = videoObject => {
  if (isNil(videoObject)) {
    throw new Error('video not defined');
  }
  return get(videoObject, 'description', '');
};

/**
 * Gets video creation time.
 *
 * @param {object} videoObject - The video object.
 * @returns {string} The video creation time or an empty string if createdAt is undefined.
 * @throws Error if video object is undefined.
 */
export const getCreation = videoObject => {
  if (isNil(videoObject)) {
    throw new Error('video not defined');
  }
  return get(videoObject, 'createdAt', '');
};

/**
 * Gets video last update time.
 *
 * @param {object} videoObject - The video object.
 * @returns {string} The video last update time or an empty string if updatedAt is undefined.
 * @throws Error if video object is undefined.
 */
export const getLastUpdate = videoObject => {
  if (isNil(videoObject)) {
    throw new Error('video not defined');
  }
  return get(videoObject, 'updatedAt', '');
};

/**
 * Gets video audience from video object.
 *
 * @param {object} videoObject - The video object.
 * @returns {string} The video audience or an empty string if audience is undefined or empty.
 * @throws Error if video object is undefined.
 */
export const getAudience = videoObject => {
  if (isNil(videoObject)) {
    throw new Error('video not defined');
  }
  return get(videoObject, 'audience[0]', '');
};

/**
 * Gets video url from video object.
 *
 * @param {object} videoObject - The video object.
 * @returns {string} The video url or an empty string if url is undefined.
 * @throws Error if video object is undefined.
 */
export const getURL = videoObject => {
  if (isNil(videoObject)) {
    throw new Error('video not defined');
  }
  return get(videoObject, 'url', '');
};

/**
 * Gets video geometry file name from video object.
 *
 * @param {object} videoObject - The video object.
 * @returns {string} The video geometry file name or an empty string if it is undefined.
 * @throws Error if video object is undefined.
 */
export const getGeometryFileName = videoObject => {
  if (isNil(videoObject)) {
    throw new Error('video not defined');
  }
  return get(videoObject, 'geometryFileName', '');
};

/**
 * Gets video logFileName from video object.
 *
 * @param {object} videoObject - The video object.
 * @returns {string} The video logFileName or an empty string if logFileName is undefined.
 * @throws Error if video object is undefined.
 */
export const getLogFileName = videoObject => {
  if (isNil(videoObject)) {
    throw new Error('video not defined');
  }
  return get(videoObject, 'logFileName', '');
};

/**
 * Gets video latLng0 from video object.
 *
 * @param {object} videoObject - The video object.
 * @returns {Array} The video latLng0 or an empty array if latLng0 is undefined.
 * @throws Error if video object is undefined.
 */
export const getLatLng0 = videoObject => {
  if (isNil(videoObject)) {
    throw new Error('video not defined');
  }
  return get(videoObject, 'latLng0', []);
};

/**
 * Gets video flightDate from video object.
 *
 * @param {object} videoObject - The video object.
 * @returns {string} The video flightDate or an empty string if flightDate is undefined.
 * @throws Error if video object is undefined.
 */
export const getFlightDate = videoObject => {
  if (isNil(videoObject)) {
    throw new Error('video not defined');
  }
  return get(videoObject, 'flightDate', '');
};

/**
 * Gets video option from video object.
 *
 * @param {object} videoObject - The video object.
 * @returns {string} The video option or an empty string if option is undefined.
 * @throws Error if video object is undefined.
 */
export const getOption = videoObject => {
  if (isNil(videoObject)) {
    throw new Error('video not defined');
  }
  return get(videoObject, 'option', '');
};

/**
 * Checks if video option is Start Position.
 *
 * @param {object} videoObject - The video object.
 * @returns {boolean} True if video option is Start Position, false otherwise.
 * @throws Error if video object is undefined.
 */
export const isStartPosition = videoObject => {
  if (isNil(videoObject)) {
    throw new Error('video not defined');
  }
  return get(videoObject, 'option', '') === VIDEO_OPTIONS.OPT0;
};

/**
 * Checks if video option is Device Position.
 *
 * @param {object} videoObject - The video object.
 * @returns {boolean} True if video option is Device Position, false otherwise.
 * @throws Error if video object is undefined.
 */
export const isDevicePosition = videoObject => {
  if (isNil(videoObject)) {
    throw new Error('video not defined');
  }
  return get(videoObject, 'option', '') === VIDEO_OPTIONS.OPT1;
};

/**
 * Checks if video option is Device Position + Focus Point.
 *
 * @param {object} videoObject - The video object.
 * @returns {boolean} True if video option is Device Position + Focus Point, false otherwise.
 * @throws Error if video object is undefined.
 */
export const isDevicePositionAndFocusPoint = videoObject => {
  if (isNil(videoObject)) {
    throw new Error('video not defined');
  }
  return get(videoObject, 'option', '') === VIDEO_OPTIONS.OPT2;
};

/**
 * Checks if video option is Covered Surface.
 *
 * @param {object} videoObject - The video object.
 * @returns {boolean} True if video option is Covered Surface, false otherwise.
 * @throws Error if video object is undefined.
 */
export const isCoveredSurface = videoObject => {
  if (isNil(videoObject)) {
    throw new Error('video not defined');
  }
  return get(videoObject, 'option', '') === VIDEO_OPTIONS.OPT3;
};

/**
 * Checks if video option is Projected Video.
 *
 * @param {object} videoObject - The video object.
 * @returns {boolean} True if video option is Projected Video, false otherwise.
 * @throws Error if video object is undefined.
 */
export const isProjectedVideo = videoObject => {
  if (isNil(videoObject)) {
    throw new Error('video not defined');
  }
  return get(videoObject, 'option', '') === VIDEO_OPTIONS.OPT4;
};

/**
 * Gets video type from video object.
 *
 * @param {object} videoObject - The video object.
 * @returns {string} The video type or an empty string if type is undefined.
 * @throws Error if video object is undefined.
 */
export const getType = videoObject => {
  if (isNil(videoObject)) {
    throw new Error('video not defined');
  }
  return get(videoObject, 'type', '');
};

/**
 * Checks if video type is drone.
 *
 * @param {object} videoObject - The video object.
 * @returns {boolean} True if video type is drone, false otherwise.
 * @throws Error if video object is undefined.
 */
export const isDrone = videoObject => {
  if (isNil(videoObject)) {
    throw new Error('video not defined');
  }
  return get(videoObject, 'type', '') === VIDEO_TYPES.DRONE;
};

/**
 * Checks if video type is mobile.
 *
 * @param {object} videoObject - The video object.
 * @returns {boolean} True if video type is mobile, false otherwise.
 * @throws Error if video object is undefined.
 */
export const isMobile = videoObject => {
  if (isNil(videoObject)) {
    throw new Error('video not defined');
  }
  return get(videoObject, 'type', '') === VIDEO_TYPES.MOBILE;
};

/**
 * Checks if video type is car.
 *
 * @param {object} videoObject - The video object.
 * @returns {boolean} True if video type is car, false otherwise.
 * @throws Error if video object is undefined.
 */
export const isCar = videoObject => {
  if (isNil(videoObject)) {
    throw new Error('video not defined');
  }
  return get(videoObject, 'type', '') === VIDEO_TYPES.CAR;
};

/**
 * Gets video tags from video object.
 *
 * @param {object} videoObject - The video object.
 * @returns {Array} The video tags or an empty array if tags is undefined.
 * @throws Error if video object is undefined.
 */
export const getTags = videoObject => {
  if (isNil(videoObject)) {
    throw new Error('video not defined');
  }
  return get(videoObject, 'tags', []);
};

/**
 * Gets video track from video object.
 *
 * @param {object} videoObject - The video object.
 * @returns {object} The video track or an empty object if track is undefined.
 * @throws Error if video object is undefined.
 */
export const getTrack = videoObject => {
  if (isNil(videoObject)) {
    throw new Error('video not defined');
  }
  return get(videoObject, 'track', {});
};

/**
 * Gets video control points from video object.
 *
 * @param {object} videoObject - The video object.
 * @returns {object} The video control points or an empty object if controlPoints is undefined.
 * @throws Error if video object is undefined.
 */
export const getControlPoints = videoObject => {
  if (isNil(videoObject)) {
    throw new Error('video not defined');
  }
  return get(videoObject, 'controlPoints', {});
};

/**
 * Gets video coordinates data type from video object.
 *
 * @param {object} videoObject - The video object.
 * @returns {string} The video coordinates data type or an empty string if coordinatesDataType is undefined.
 * @throws Error if video object is undefined.
 */
export const getCoordinatesDataType = videoObject => {
  if (isNil(videoObject)) {
    throw new Error('video not defined');
  }
  return get(videoObject, 'coordinatesDataType', '');
};

/**
 * Gets video annotation templates from video object.
 *
 * @param {object} videoObject - The video object.
 * @returns {object[]} The video annotation templates or an empty array if annotationTemplates is undefined.
 * @throws Error if video object is undefined.
 */
export const getAnnotationTemplates = videoObject => {
  if (isNil(videoObject)) {
    throw new Error('video not defined');
  }
  return get(videoObject, 'annotationTemplates', []);
};

/**
 * Checks if video coordinates data type is point.
 *
 * @param {object} videoObject - The video object.
 * @returns {boolean} Returns true if video coordinates data type is point and false otherwise.
 * @throws Error if video object is undefined.
 */
export const areCoordinatesFromPoint = videoObject => {
  if (isNil(videoObject)) {
    throw new Error('video not defined');
  }
  return get(videoObject, 'coordinatesDataType', '') === VIDEO_COORDINATE_DATA_TYPES.POINT;
};

/**
 * Checks if video coordinates data type is line.
 *
 * @param {object} videoObject - The video object.
 * @returns {boolean} Returns true if video coordinates data type is line and false otherwise.
 * @throws Error if video object is undefined.
 */
export const areCoordinatesFromLine = videoObject => {
  if (isNil(videoObject)) {
    throw new Error('video not defined');
  }
  return get(videoObject, 'coordinatesDataType', '') === VIDEO_COORDINATE_DATA_TYPES.LINE;
};

/**
 * Checks if video coordinates data type is kml.
 *
 * @param {object} videoObject - The video object.
 * @returns {boolean} Returns true if video coordinates data type is kml and false otherwise.
 * @throws Error if video object is undefined.
 */
export const areCoordinatesFromKML = videoObject => {
  if (isNil(videoObject)) {
    throw new Error('video not defined');
  }
  return get(videoObject, 'coordinatesDataType', '') === VIDEO_COORDINATE_DATA_TYPES.KML;
};

/**
 * Checks if video coordinates data type is csv.
 *
 * @param {object} videoObject - The video object.
 * @returns {boolean} Returns true if video coordinates data type is csv and false otherwise.
 * @throws Error if video object is undefined.
 */
export const areCoordinatesFromCSV = videoObject => {
  if (isNil(videoObject)) {
    throw new Error('video not defined');
  }
  return get(videoObject, 'coordinatesDataType', '') === VIDEO_COORDINATE_DATA_TYPES.CSV;
};

/**
 * Checks if video coordinates data type is csvGoPro.
 *
 * @param {object} videoObject - The video object.
 * @returns {boolean} Returns true if video coordinates data type is csvGoPro and false otherwise.
 * @throws Error if video object is undefined.
 */
export const areCoordinatesFromGoProCSV = videoObject => {
  if (isNil(videoObject)) {
    throw new Error('video not defined');
  }
  return get(videoObject, 'coordinatesDataType', '') === VIDEO_COORDINATE_DATA_TYPES.CSV_GO_PRO;
};

/**
 * Checks if the video is shared.
 *
 * @param {object} videoObject - The video object.
 * @returns {boolean} Returns true if the video is shared and false otherwise.
 * @throws Error if video object is undefined.
 */
export const isShared = videoObject => {
  if (isNil(videoObject)) {
    throw new Error('video not defined');
  }
  return get(videoObject, 'shared', false);
};

/**
 * Checks if the video is shared.
 *
 * @param {object} videoObject - The video object.
 * @returns {boolean} Returns true if the video is shared and false otherwise.
 * @throws Error if video object is undefined.
 */
export const isSharedPrivate = videoObject => {
  if (isNil(videoObject)) {
    throw new Error('video not defined');
  }
  return get(videoObject, 'sharedPrivate', false);
};

/**
 * Checks if the video is deleted.
 *
 * @param {object} videoObject - The video object.
 * @returns {boolean} Returns true if the video is deleted and false otherwise.
 * @throws Error if video object is undefined.
 */
export const isDeleted = videoObject => {
  if (isNil(videoObject)) {
    throw new Error('video not defined');
  }
  return get(videoObject, 'deleted', '') !== 'no';
};

/**
 * Checks if the video corner coordinates are undefined.
 *
 * @param {object} videoObject - The video object.
 * @returns {boolean} Returns true if the video corner coordinates are undefined and false otherwise.
 * @throws Error if video object is undefined.
 */
export const areCoordinatesUndefined = videoObject => {
  if (isNil(videoObject)) {
    throw new Error('video not defined');
  }
  return get(videoObject, 'noCoordinates', false);
};

/**
 * Checks if the video is represented by line or not.
 *
 * @param {object} videoObject - The video object.
 * @returns {boolean} Returns true if the video is represented by line.
 * @throws Error if video object is undefined.
 */
export const isVideoLine = videoObject => {
  if (isNil(videoObject)) {
    throw new Error('video not defined');
  }
  const existingTrack = !isEmpty(getTrack(videoObject));
  return existingTrack;
};

/**
 * Get the map base selected for geomotion video.
 *
 * @param {object} videoObject - The video object.
 * @returns {object} Returns object with base maps.
 * @throws Error if video object is undefined.
 */
export const getGeomotionMap = videoObject => {
  if (isNil(videoObject)) {
    throw new Error('video not defined');
  }
  return get(videoObject, 'geomotionMap', {});
};
